import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Folder as CasesIcon,
  AttachMoney as MoneyIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchReportsSummary,
  fetchCasesByType,
  fetchLawyerPerformance,
  exportPdfReport,
  exportExcelReport,
  setSelectedPeriod,
  setCustomDateRange,
  selectReportsSummary,
  selectCasesByType,
  selectLawyerPerformance,
  selectReportsLoading,
  selectReportsError,
  selectSelectedPeriod,
  selectCustomDateRange,
} from '../store/slices/reportsSlice';
import { fetchCases } from '../store/slices/casesSlice';
import { getCurrentUserFromStorage } from '../store/slices/authSlice';
import StatusBadge, { getStatusFilterOptions, getStatusConfig } from '../components/common/StatusBadge';

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const summary = useAppSelector(selectReportsSummary);
  const casesByType = useAppSelector(selectCasesByType);
  const lawyerPerformance = useAppSelector(selectLawyerPerformance);
  const loading = useAppSelector(selectReportsLoading);
  const error = useAppSelector(selectReportsError);
  const selectedPeriod = useAppSelector(selectSelectedPeriod);
  const customDateRange = useAppSelector(selectCustomDateRange);
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const cases = useAppSelector((state: any) => state.cases.cases);

  const currentUser = getCurrentUserFromStorage();
  const userId = currentUser?.user_id; // Fallback to 1 if no user found

  useEffect(() => {
    if (userId) {
      dispatch(fetchCases({ userId }) as any);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    const fetchAllReports = async () => {
      try {
        const dateRange = selectedPeriod === 'custom' ? customDateRange : undefined;
        await Promise.all([
          dispatch(fetchReportsSummary({ userId: userId!, period: selectedPeriod, customDateRange: dateRange })),
          dispatch(fetchCasesByType({ userId: userId!, period: selectedPeriod, customDateRange: dateRange })),
          dispatch(fetchLawyerPerformance({ userId: userId!, period: selectedPeriod, customDateRange: dateRange })),
        ]);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchAllReports();
  }, [dispatch, userId, selectedPeriod, customDateRange]);

  const handlePeriodChange = (newPeriod: string) => {
    dispatch(setSelectedPeriod(newPeriod as any));
  };

  const handleRefresh = () => {
    const dateRange = selectedPeriod === 'custom' ? customDateRange : undefined;
    dispatch(fetchReportsSummary({ userId: userId!, period: selectedPeriod, customDateRange: dateRange }));
    dispatch(fetchCasesByType({ userId: userId!, period: selectedPeriod, customDateRange: dateRange }));
    dispatch(fetchLawyerPerformance({ userId: userId!, period: selectedPeriod, customDateRange: dateRange }));
  };

  const handleExportPdf = () => {
    const dateRange = selectedPeriod === 'custom' ? customDateRange : undefined;
    dispatch(exportPdfReport({ userId: userId!, period: selectedPeriod, customDateRange: dateRange }));
  };

  const handleExportExcel = () => {
    const dateRange = selectedPeriod === 'custom' ? customDateRange : undefined;
    dispatch(exportExcelReport({ userId: userId!, period: selectedPeriod, customDateRange: dateRange }));
  };

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      dispatch(setCustomDateRange({
        ...customDateRange,
        from: date.toISOString().split('T')[0]
      }));
    }
  };

  const handleToDateChange = (date: Date | null) => {
    if (date) {
      dispatch(setCustomDateRange({
        ...customDateRange,
        to: date.toISOString().split('T')[0]
      }));
    }
  };

  const stats = [
    { 
      title: t('reports.totalCases'), 
      value: summary?.total_cases?.value?.toLocaleString() || '0', 
      change: summary?.total_cases?.change ? `${summary.total_cases.change > 0 ? '+' : ''}${summary.total_cases.change}%` : '+0%', 
      icon: <CasesIcon />, 
      color: '#2563eb',
      changeType: summary?.total_cases?.change_type || 'increase'
    },
    { 
      title: t('reports.totalRevenue'), 
      value: summary?.total_revenue?.value ? `$${(summary.total_revenue.value / 1000).toFixed(0)}K` : '$0', 
      change: summary?.total_revenue?.change ? `${summary.total_revenue.change > 0 ? '+' : ''}${summary.total_revenue.change}%` : '+0%', 
      icon: <MoneyIcon />, 
      color: '#16a34a',
      changeType: summary?.total_revenue?.change_type || 'increase'
    },
    { 
      title: t('reports.activeClients'), 
      value: summary?.active_clients?.value?.toLocaleString() || '0', 
      change: summary?.active_clients?.change ? `${summary.active_clients.change > 0 ? '+' : ''}${summary.active_clients.change}%` : '+0%', 
      icon: <PeopleIcon />, 
      color: '#9333ea',
      changeType: summary?.active_clients?.change_type || 'increase'
    },
    { 
      title: t('reports.successRate'), 
      value: summary?.success_rate?.value ? `${summary.success_rate.value.toFixed(1)}%` : '0%', 
      change: summary?.success_rate?.change ? `${summary.success_rate.change > 0 ? '+' : ''}${summary.success_rate.change}%` : '+0%', 
      icon: <AssessmentIcon />, 
      color: '#f97316',
      changeType: summary?.success_rate?.change_type || 'increase'
    },
  ];

  const isLoading = loading.summary || loading.casesByType || loading.lawyerPerformance || loading.export;

  const statusBreakdown = getStatusFilterOptions(t).map((option) => {
    const count = cases.filter(
      (c: any) => (c.status || '').toLowerCase() === option.value,
    ).length;
    return {
      ...option,
      color: getStatusConfig(option.value).color,
      count,
      percentage: cases.length ? Math.round((count / cases.length) * 100) : 0,
    };
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('navigation.reports')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField 
              select 
              size="small" 
              value={selectedPeriod} 
              onChange={(e) => handlePeriodChange(e.target.value)} 
              sx={{ width: 160 }}
              disabled={isLoading}
            >
              <MenuItem value="this_month">{t('reports.thisMonth')}</MenuItem>
              <MenuItem value="this_quarter">{t('reports.thisQuarter')}</MenuItem>
              <MenuItem value="this_year">{t('reports.thisYear')}</MenuItem>
              <MenuItem value="last_year">{t('reports.lastYear')}</MenuItem>
              <MenuItem value="custom">{t('reports.customRange')}</MenuItem>
            </TextField>
            {selectedPeriod === 'custom' && (
              <>
                <DatePicker
                  label={t('reports.fromDate')}
                  value={customDateRange.from ? new Date(customDateRange.from) : null}
                  onChange={handleFromDateChange}
                  disabled={isLoading}
                  slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                />
                <DatePicker
                  label={t('reports.toDate')}
                  value={customDateRange.to ? new Date(customDateRange.to) : null}
                  onChange={handleToDateChange}
                  disabled={isLoading}
                  slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                />
              </>
            )}
          <Tooltip title={t('reports.refreshData')}>
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reports.exportPdf')}>
            <IconButton onClick={handleExportPdf} disabled={isLoading || loading.export}>
              {loading.export ? <CircularProgress size={24} /> : <PdfIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reports.exportExcel')}>
            <IconButton onClick={handleExportExcel} disabled={isLoading || loading.export}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedPeriod === 'custom' && (!customDateRange.from || !customDateRange.to) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('reports.selectDateRange')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1.5 }}>
        {stats.map((stat) => (
          <Box key={stat.title} sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                {isInitialLoad ? (
                  <Skeleton variant="rectangular" height={120} />
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ bgcolor: stat.color + '14', color: stat.color }}>{stat.icon}</Avatar>
                      <Chip 
                        label={stat.change} 
                        size="small" 
                        color={stat.changeType === 'increase' ? 'success' : 'error'} 
                      />
                    </Box>
                    <Typography variant="h4" sx={{ mb: 0.5 }}>{stat.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, mx: -1.5 }}>
        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>{t('reports.casesByType')}</Typography>
              {isInitialLoad ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <>
                {casesByType.length > 0 && (
                  <BarChart
                    height={220}
                    xAxis={[{ scaleType: 'band', data: casesByType.map((row) => row.type) }]}
                    series={[{ data: casesByType.map((row) => row.cases), color: '#2563eb' }]}
                    sx={{ mb: 2 }}
                  />
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.type')}</TableCell>
                        <TableCell align="center">{t('reports.cases')}</TableCell>
                        <TableCell>{t('reports.distribution')}</TableCell>
                        <TableCell align="right">{t('reports.revenue')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {casesByType.map((row) => (
                        <TableRow key={row.type}>
                          <TableCell>{row.type}</TableCell>
                          <TableCell align="center">{row.cases}</TableCell>
                          <TableCell sx={{ width: 150 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress variant="determinate" value={row.distribution} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                              <Typography variant="caption">{row.distribution}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">${row.revenue.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>{t('reports.lawyerPerformance')}</Typography>
              {isInitialLoad ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.lawyer')}</TableCell>
                        <TableCell align="center">{t('reports.cases')}</TableCell>
                        <TableCell align="center">{t('reports.completed')}</TableCell>
                        <TableCell align="right">{t('reports.revenue')}</TableCell>
                        <TableCell align="center">{t('reports.rating')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lawyerPerformance.map((row) => (
                        <TableRow key={row.user_id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                                {row.name.split(' ').map((n) => n[0]).join('')}
                              </Avatar>
                              {row.name}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{row.cases_count}</TableCell>
                          <TableCell align="center">{row.completed_cases}</TableCell>
                          <TableCell align="right">${row.revenue.toLocaleString()}</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={row.avg_satisfaction.toFixed(1)} 
                              size="small" 
                              color={row.avg_satisfaction >= 4.7 ? 'success' : 'primary'} 
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>{t('reports.statusBreakdown', 'Status Breakdown')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {statusBreakdown.map((status) => (
                <Box key={status.value} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 140, flexShrink: 0 }}>
                    <StatusBadge status={status.value} />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={status.percentage}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { bgcolor: status.color },
                    }}
                  />
                  <Typography variant="body2" sx={{ width: 90, textAlign: 'right', flexShrink: 0 }}>
                    {status.count} ({status.percentage}%)
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
